<template>
  <div>
    <!-- Country -->
    <div class="wwf-field">
      <select
        name="country"
        v-model="donation.addressInput.country"
        v-validate="'required'"
        :class="{
          error: errors.has('country'),
          empty: !donation.addressInput.country,
        }"
        autocomplete="country"
      >
        <option v-for="countries in countryList" :value="countries.name">
          {{ countries.name }}
        </option>
      </select>
      <label for="title">Country</label>
    </div>

    <!-- Autocomplete field -->
    <div id="autocomplete-form" v-if="isAutocompleteAvailable">
      <form autocomplete="off" v-if="!formattedAddress">
        <SensisAutocomplete
          ref="donateSensisAutocomplete"
          id="donate-sensis-autocomplete"
          :showNotFound="true"
          placeholder="Find address"
          @addressSelected="addressSelected"
        ></SensisAutocomplete>
      </form>

      <div v-if="!!formattedAddress">
        <label>
          <textarea v-model="formattedAddress" disabled> </textarea>
        </label>

        <button class="discreet" type="button" v-on:click="changeAddress()">
          Change my address
        </button>
      </div>
    </div>

    <button
      name="enter-manual-address"
      class="discreet"
      type="button"
      v-on:click="manualInput = true"
      v-if="isAutocompleteAvailable"
    >
      Enter address manually
    </button>

    <!-- Manual Input -->
    <div v-if="showManualInput">
      <form autocomplete="off">
        <!-- Unit Number -->
        <div class="wwf-field">
          <input
            id="unit-number"
            name="unit-number"
            type="text"
            v-model="address.unitNumber"
            :class="{
              empty: !address.unitNumber,
            }"
            autocomplete="off"
          />
          <label for="unit-number">Unit Number</label>
        </div>

        <!-- Street Number -->
        <div class="wwf-field">
          <input
            id="street-number"
            name="street-number"
            type="text"
            v-model="address.streetNumber"
            v-validate="'required'"
            :class="{
              error: errors.has('street-number'),
              empty: !address.streetNumber,
            }"
            autocomplete="off"
          />
          <label for="street-number">Street Number</label>
        </div>

        <!-- Street Name -->
        <div class="wwf-field">
          <input
            id="street-name"
            name="street-name"
            type="text"
            v-model="address.streetName"
            v-validate="'required'"
            :class="{
              error: errors.has('street-name'),
              empty: !address.streetName,
            }"
            autocomplete="off"
          />
          <label for="street-name">Street Name</label>
        </div>

        <!-- Suburb -->
        <div class="wwf-field">
          <input
            id="suburb"
            name="suburb"
            type="text"
            v-model="address.suburb"
            v-validate="'required'"
            :class="{ error: errors.has('suburb'), empty: !address.suburb }"
            autocomplete="off"
          />
          <label for="suburb">Suburb</label>
        </div>

        <!-- Postcode -->
        <div class="wwf-field">
          <input
            id="postcode"
            name="postcode"
            type="text"
            v-model="address.postcode"
            v-validate="{ required: true, regex:/^[\d\w\s-()]*[^\s]$/}"
            :class="{ error: errors.has('postcode'), empty: !address.postcode }"
            autocomplete="off"
          />
          <label for="postcode">Postcode</label>
        </div>

        <!-- State -->
        <div v-if="isAustralia" class="wwf-field">
          <select
            name="state"
            v-model="address.state"
            v-validate="'required'"
            :class="{ error: errors.has('state'), empty: !address.state }"
            autocomplete="off"
          >
            <option value disabled selected>Select a state</option>
            <option value="NSW">NSW</option>
            <option value="ACT">ACT</option>
            <option value="NT">NT</option>
            <option value="SA">SA</option>
            <option value="WA">WA</option>
            <option value="VIC">VIC</option>
            <option value="QLD">QLD</option>
            <option value="TAS">TAS</option>
          </select>
          <label for="state">State</label>
        </div>

        <div v-if="!isAustralia" class="wwf-field">
          <input
            id="state"
            name="state"
            type="text"
            v-model="address.state"
            v-validate="{ required: false }"
            :class="{ error: errors.has('state'), empty: !address.state }"
            placeholder="State"
            autocomplete="off"
          />
          <label for="state">State</label>
        </div>
      </form>

      <button
        class="discreet"
        type="button"
        v-on:click="manualInput = false"
        v-if="canFindMyAddress"
      >
        Find my address
      </button>
    </div>
  </div>
</template>

<script>
import SensisAutocomplete from "../SensisAutocomplete.vue";

import { mapState } from "vuex";
import { getCountriesList } from "../../helpers";

export default {
  name: "AddressInput",
  components: {
    SensisAutocomplete,
  },

  data() {
    return {
      countryList: getCountriesList(),
      manualInput: false,
      autocompleteAddress: null,
      formattedAddress: null,
      address: {
        unitNumber: null,
        streetNumber: null,
        streetName: null,
        suburb: null,
        postcode: null,
        state: null,
      },
    };
  },
  computed: {
    showManualInput() {
      return this.manualInput || this.country !== "Australia";
    },
    isAustralia() {
      return this.country === "Australia";
    },
    isAutocompleteAvailable() {
      return this.country === "Australia" && !this.manualInput;
    },
    canFindMyAddress() {
      return this.country === "Australia";
    },
    ...mapState({
      donation: (state) => state.donation.donation,
      country: (state) => state.donation.donation.addressInput.country,
    }),
  },

  watch: {
    country() {
      this.address.state = null;
    },
  },
  methods: {
    addressSelected(address, formatted, addressNotFound) {
      console.log("addressNotFound", addressNotFound);
      if (addressNotFound) {
        this.manualInput = true;
      }

      if (!addressNotFound) {
        this.formattedAddress = formatted;
        this.autocompleteAddress = address;
      }
    },
    changeAddress() {
      this.formattedAddress = null;
      this.autocompleteAddress = null;
    },
    validate() {
      return new Promise((resolve, reject) => {
        if (this.isAutocompleteAvailable) {
          // Autocomplete
          if (!!this.autocompleteAddress) {
            resolve(true);
          } else {
            this.$refs.donateSensisAutocomplete.validate();
            reject(false);
          }
        } else {
          this.validateManual()
            .then((val) => resolve(val))
            .catch((err) => reject(err));
        }
      });
    },
    validateManual() {
      console.log("Validating manual address");
      return new Promise((resolve, reject) => {
        this.$validator
          .validateAll()
          .then((result) => {
            // Will return true if valid and false if not
            if (result) {
              resolve(result);
            } else {
              console.error("Address Input not valid - result", result);
              reject(result);
            }
          })
          .catch((err) => {
            console.error("Address Input Validation - Error - ", err);
            reject(err);
          });
      });
    },
    getAddressDetails() {
      if (!!this.autocompleteAddress) {
        return {
          ...this.autocompleteAddress,
          isVerified: true,
        };
      }
      return {
        unitnumber: this.address.unitNumber,
        streetno: this.address.streetNumber,
        streetname: this.address.streetName,
        postcode: this.address.postcode,
        state: this.address.state,
        suburb: this.address.suburb,
        country: this.country,
        isVerified: false,
      };
    },
  },
};
</script>

<style scoped>
#autocomplete-form {
  margin-bottom: 1rem;
}

.donation-form.wwf-form textarea {
  padding: 15px;
}

.donation-form.wwf-form textarea:disabled {
  resize: none;
  border: none;
  outline: none;
  background-color: var(--wwfGreyDE);
  cursor: default !important;
}

.donation-form.wwf-form button.discreet {
  padding: 0;
  font-size: 14px;
  line-height: 18px;
  text-transform: none;
}
</style>


// WEBPACK FOOTER //
// AddressInput.vue?6fe48a28